/**
 * The Family Portal's values, ported from the design's renderVals (L39476-40240), the
 * family setup (L41315) and the portal's data block (L35264-35357). The design's sample
 * records (FP_NOTICES, FP_VISITS, FP_CIRCLE, FP_PLAN, FP_MEDS, the thread, the panels) are
 * here the page's model, built from the routes by buildModel() in FamilyPortal.tsx.
 */
import { pre } from './dom';

type Ev = { preventDefault?: () => void } | undefined;

/** How the viewer is listed, in words: "poa" reads "POA (power of attorney)". */
export const relWords = (rel: string): string => {
  const r = rel.trim().toLowerCase();
  return r === 'poa' ? 'POA (power of attorney)' : r === 'hcpoa' ? 'health care POA' : rel;
};

export const DASH = '–';

/** One notice the family was given (design FP_NOTICES). */
export interface FpNotice {
  id: string; k: string; when: string; told: string; by: string; cls: string; sev: 'high' | 'med' | 'low'; med: boolean; mine: boolean;
  /** The notice kind (fall, med_change, infection...); optional for older callers. */
  kind?: string;
  /** short: "Ruth K."; at: when they confirmed it ("6:52 PM"). */
  seenBy: Array<{ name: string; seen: boolean; short?: string; at?: string | null }>;
}
export interface FpVisit { who: string; when: string; days: number; note: string; mine: boolean }
export interface FpCircleMember { who: string; rel: string; role: string; state: 'VERIFIED' | 'INVITED'; sees: string; last: string; lastShort: string; mine: boolean; inviteId?: string | null }
export interface FpPlanGoal { goal: string; how: string; on: boolean; evidence: string; basis: string; source: string; pct: number; next: string; owner: string }
export interface FpMessage { mine: boolean; body: string; meta: string }

export interface FpModel {
  /** Family (the resident's own circle) or a staff reader. */
  fam: boolean;
  role: 'Family' | 'Operator' | 'Facility Admin';
  self: { name: string; rel: string };
  resident: { id: string; name: string; first: string; initials: string; room: string; facility: string; atHome?: boolean } | null;
  notices: FpNotice[];
  thread: FpMessage[];
  threadSeen: string;
  threadsExtra: Array<{ key: 'desk' | 'family'; label: string; last: string; meta: string; seenBy: string; placeholder: string }>;
  visits: FpVisit[];
  circle: FpCircleMember[];
  plan: FpPlanGoal[];
  planMeeting: string;
  planGap: string;
  planReview: string;
  meds: Array<{ name: string; rule: string; why: string }>;
  moodSeries: number[];
  fall: { score: number | null; conf: string };
  /** w: the points the driver added to the score; null when the server did not say (no bar, an en dash). */
  drivers: Array<{ label: string; w: number | null }>;
  healthSeries: number[];
  healthNote: string;
  healthBasis: Array<{ label: string; basis: string; note: string }>;
  healthAction: { family: string; staff: string; ownerFamily: string; ownerStaff: string } | null;
  summaryLines: string[];
  doctor: string;
  moments: Array<{ cap: string; who: string; when: string; bg: string }>;
  dates: Array<{ id: string; mon: string; day: string; what: string; inDays: string; note: string; remindResident: boolean; remindMe: boolean }>;
  coming: Array<{ when: string; what: string; state: string; at: string }>;
  tasks: Array<{ id: string; t: string; who: string | null; mine: boolean; done: boolean }>;
  events: Array<{ id: string; kind: string; head: string; when: string; urgent: boolean; how: string; did: string; watch: string; owner: string; acknowledged: boolean }>;
  panels: Array<{ label: string; value: string; basis: string; trend: string; why: string; action: string; owner: string; series: number[] }>;
  appt: { chain: Array<{ label: string; sub: string }>; items: Array<{ said: string; heard: string; verdict: string; why: string; action: string; owner: string; done: boolean }> } | null;
  stats: { visits30d: number | null; checkInsWeek: number | null; openRequests: number | null };
  summary: string;
  careNote: string;
  careBy: string;
  firstMonth: string;
  highlights: Array<{ text: string; when: string }>;
  weekly: boolean;
  /** Staff: coverage by building and the family-message state. */
  coverage: Array<{ name: string; residents: number; represented: number }>;
  inbox: { open: number; sla: string; routed: string } | null;
  photoSent: boolean;
  photoNote: string | null;
  /** V4 To do (GET /family/todo): null until the route answers. */
  todo: FpTodo | null;
}

/** One notice's checklist on the To do (design fpTodoVals EV, L37298). */
export interface FpTodoEvent {
  id: string;
  kind: string;
  title: string;
  when: string;
  by: string;
  redacted: boolean;
  seen: boolean;
  /** Null: the care team has not written the steps yet (reason says so). */
  steps: Array<{ id: string; t: string; d: string; act: string | null; kind: string; facility: boolean; done: boolean }> | null;
  reason: string | null;
}
export interface FpTodo {
  events: FpTodoEvent[];
  nudges: Array<{ key: string; kind: 'no_visit' | 'birthday' | 'photo'; who: string | null; days: number | null; label: string | null; date: string | null; doneAt: string | null }>;
  askName: string | null;
}

export interface FpUi {
  tab: 'home' | 'access' | 'today' | 'visits' | 'appt' | 'plan' | 'messages' | 'health' | 'moments' | 'dates' | 'tasks';
  threadKey: 'care' | 'desk' | 'family';
  draft: string;
  sent: Array<{ body: string; to: string }>;
  code: string;
  codeMsg: string;
  accessMsg: string;
  visitAsk: string;
  planRaised: Record<number, boolean>;
  apptDone: Record<number, boolean>;
  quickUsed: Record<string, boolean>;
  panelAck: Record<string, boolean>;
  eventSeen: Record<string, boolean>;
  medOut: Record<number, string | null>;
  summaryOpen: boolean;
  askedMeeting: boolean;
  /** The To do's "Add a to-do" box. */
  tdNew: string;
  /** Staff: notices whose family was phoned here (noticeId to the time). */
  called: Record<string, string>;
}

export interface FpActs {
  setUi: (p: Partial<FpUi>) => void;
  requestLink: (code: string) => void;
  send: (body: string, to: FpUi['threadKey']) => void;
  ask: (body: string) => void;
  noticeSeen: (n: FpNotice) => void;
  eventAck: (id: string) => void;
  panelSeen: (label: string) => void;
  photo: (file: File) => void;
  taskAct: (id: string, action: 'claim' | 'done') => void;
  remind: (id: string, patch: { remindResident?: boolean; remindMe?: boolean }) => void;
  weekly: (on: boolean) => void;
  meeting: () => void;
  /** `onOk` runs only once the server has the request: nothing says "requested" before that. */
  familyRequest: (kind: string, text: string, onOk?: () => void) => void;
  apptDone: (i: number) => void;
  medOutcome: (i: number, name: string, outcome: string | null) => void;
  openResidents: () => void;
  /** Opens her Life Story as a PDF (GET /family/:residentId/life-story.pdf). */
  lifeStory: () => void;
  resendInvite: (inviteId: string, who: string) => void;
  /** To do: tick one step of a notice's checklist (POST /family/todo/steps/:id/done). */
  stepDone: (stepId: string) => void;
  /** To do: a Staying close nudge acted on. */
  nudgeDone: (key: string) => void;
  /** To do: "Add a to-do" for the whole family. */
  taskAdd: (text: string) => void;
  /** Staff: the family was phoned about a notice nobody confirmed. */
  noticeCalled: (n: FpNotice) => void;
}

/** The design's fpSpark (L35309): 14 bars against the resident's own band, in-band bars quiet. */
export function fpBars(series: number[], t: string) {
  return series.map((v, i) => {
    const inBand = v >= 0.4 && v <= 0.72;
    return {
      style: 'display:block;flex:1 1 0;min-width:0;align-self:flex-end;border-radius:1.5px;height:' + Math.max(8, Math.min(100, Math.round(v * 100))) + '%;background:' + (inBand ? 'rgba(34,39,46,.22)' : t) +
        ';transform-origin:bottom;animation:growH .45s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + i * 22 + 'ms;',
    };
  });
}

const OCHRE = '#5B6B7C';

export function familyVals(M: FpModel, ui: FpUi, act: FpActs, linked: boolean) {
  const role = M.role;
  const fam = M.fam;
  const R = M.resident;
  const first = R?.first ?? 'her';
  const set = (p: Partial<FpUi>) => (e: Ev) => { pre(e); act.setUi(p); };
  const askSend = (body: string) => (e: Ev) => { pre(e); act.ask(body); };
  const tabKey = ui.tab;
  const moodLast = M.moodSeries.length ? M.moodSeries[M.moodSeries.length - 1] : null;
  const band: [string, string, string] = moodLast == null ? ['Not enough yet', '#54606C', 'rgba(34,39,46,.07)']
    : moodLast >= 3.9 ? ['Bright', '#1F6B52', 'rgba(47,138,111,.13)']
      : moodLast >= 3.4 ? ['Steady', '#2F5C97', 'rgba(47,92,151,.11)']
        : moodLast >= 2.8 ? ['Quieter', '#5B6B7C', 'rgba(91,107,124,.14)'] : ['Settling', '#54606C', 'rgba(34,39,46,.07)'];
  const fs = M.fall.score;
  const tierArr: [string, string, string] = fs == null ? [DASH, '#54606C', 'rgba(34,39,46,.07)']
    : fs >= 66 ? ['HIGH', '#8C3A33', 'rgba(140,58,51,.14)'] : fs >= 40 ? ['ELEVATED', '#B3261E', 'rgba(179,38,30,.12)'] : fs >= 20 ? ['WATCH', '#5B6B7C', 'rgba(91,107,124,.14)'] : ['LOW', '#1F6B52', 'rgba(47,138,111,.13)'];

  // ── linkage ──
  const code = ui.code;
  const out: Record<string, unknown> = {
    fpLead: role === 'Operator'
      ? 'How the family surface is performing across the fleet: who is represented, who is not, and where a notice would have no recipient. Resident-level content stays behind the resident’s own consent.'
      : role === 'Facility Admin'
        ? 'How families in this building are linked and what each can see. You can see coverage and gaps: not the resident-level content a family reads, which stays behind the resident’s consent.'
        : 'A warm, read-only window into your loved one’s wellbeing, shared only as they allow. You will see how her days are going, not her clinical record.',
    fpUnlinked: !linked,
    fpLinked: linked,
    fpCode: code,
    onFpCode: (e: { target: { value: string } }) => act.setUi({ code: e.target.value, codeMsg: '' }),
    fpCodeInputStyle: 'flex:1;min-width:0;height:38px;border:1px solid rgba(34,39,46,.2);border-radius:8px;padding:0 12px;font-size:16px;color:#22272E;background:#fff;font-family:inherit;box-sizing:border-box;',
    fpReqStyle: 'display:inline-flex;align-items:center;justify-content:center;height:38px;padding:0 20px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
      (code.replace(/\s/g, '').length >= 8 ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.07);color:#54606C;'),
    onFpRequest: (e: Ev) => {
      pre(e);
      const c = code.replace(/\s/g, '');
      if (c.length < 8) { act.setUi({ codeMsg: 'A code is 8 characters. Check the one her care team gave you.' }); return; }
      act.requestLink(c);
    },
    fpCodeMsg: ui.codeMsg,
  };
  // Aithora's view is the fleet Coverage alone (Andrew, 2026-09-30): it has no resident.
  if (!R && role !== 'Operator') return out;

  // ── people & access (design L39510) ──
  const facs = M.coverage.map((f) => {
    const pct = f.residents ? Math.round((f.represented / f.residents) * 100) : 0;
    const tone = pct >= 90 ? '#1F6B52' : pct >= 75 ? OCHRE : '#B3261E';
    return {
      name: f.name, pct: pct + '%', n: f.represented + ' of ' + f.residents + ' residents',
      gap: f.residents - f.represented === 0 ? 'every resident has a representative' : f.residents - f.represented + ' with no representative on file',
      pctStyle: 'font-size:19px;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + tone + ';',
      barTrack: 'display:block;height:6px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;margin-top:6px;',
      bar: 'display:block;height:100%;border-radius:3px;transform-origin:left;animation:growW .6s cubic-bezier(.22,.61,.36,1) forwards;width:' + pct + '%;background:' + tone + ';',
      rowStyle: 'display:grid;grid-template-columns:minmax(0,1fr) minmax(120px,180px) auto;gap:13px;align-items:center;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);',
      onOpen: (e: Ev) => { pre(e); act.openResidents(); },
    };
  });
  const totRes = M.coverage.reduce((t, f) => t + f.residents, 0);
  const totRep = M.coverage.reduce((t, f) => t + f.represented, 0);
  const below = facs.filter((f) => parseInt(f.pct, 10) < 90).length;
  const stTone = (s: string) => (s === 'VERIFIED' ? '#1F6B52' : s === 'INVITED' ? OCHRE : '#B3261E');
  Object.assign(out, {
    fpAccessIsFleet: role === 'Operator',
    fpAccessIsBuilding: role === 'Facility Admin',
    fpAccessIsCircle: role === 'Family',
    fpAccessHead: role === 'Operator' ? 'Representation coverage across the fleet' : role === 'Facility Admin' ? 'Who can see each resident in this building' : 'Who else can see ' + first,
    fpAccessSub: role === 'Operator'
      ? 'A resident with no representative on file is a 483.10(g)(14) exposure and an isolation risk at once: there is nobody to notify, and nobody visiting. Coverage is the leading indicator for both.'
      : role === 'Facility Admin'
        ? 'Every resident needs a representative who can be notified. Where there is none, the notice requirement has no recipient.'
        : 'What each person can see is ' + first + '’s decision, not the building’s and not yours.',
    fpFacRows: facs,
    fpFleetStrip: [
      { k: 'Residents represented', v: totRes ? Math.round((totRep / totRes) * 100) + '%' : DASH, foot: totRep + ' of ' + totRes + ' with a verified representative', c: '#22272E' },
      { k: 'No representative', v: String(totRes - totRep), foot: 'notice requirement has no recipient', c: totRes - totRep ? '#B3261E' : '#1F6B52' },
      { k: 'Buildings below 90%', v: String(below), foot: 'coverage target for a full notice trail', c: below ? OCHRE : '#1F6B52' },
      { k: 'Basis', v: 'LIVE', foot: 'linkage records, not modeled', c: '#1F6B52', small: true },
    ].map((t) => ({ k: t.k, v: t.v, foot: t.foot, vColor: t.c, vSize: t.small ? '18px' : '26px' })),
    // People & access list (V4 fpPeopleRows: the design's two fpCircle keys collided in V3;
    // the coverage/access list reads these rows, the family's own circle reads fpCircle).
    fpPeopleRows: M.circle.map((c, i) => ({
      who: fam ? c.who : c.who.replace(/^You/, M.self.name),
      rel: c.rel, sees: c.sees, last: c.lastShort, state: c.state, role: c.role,
      stateStyle: 'display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;white-space:nowrap;color:' + stTone(c.state) + ';background:' + stTone(c.state) + '1F;',
      rowStyle: 'display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;align-items:start;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 50 + 'ms;',
      btnLabel: c.state === 'INVITED' ? 'Resend invite' : 'Change what they see',
      btnStyle: 'display:inline-flex;align-items:center;justify-content:center;height:28px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;flex:0 0 auto;' +
        (c.state === 'INVITED' ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.05);color:#3C454F;border:1px solid rgba(34,39,46,.14);'),
      onAct: (e: Ev) => {
        pre(e);
        if (c.state === 'INVITED' && c.inviteId) act.resendInvite(c.inviteId, c.who);
        else act.setUi({ accessMsg: 'Sharing for ' + c.who + ' is ' + first + '’s to change: this opens her consent record, it does not change it here.' });
      },
    })),
    fpAccessMsg: ui.accessMsg,
    fpAccessMsgStyle: ui.accessMsg ? 'font-size:14px;font-weight:600;color:#2F5C97;background:rgba(79,138,214,.10);border-radius:8px;padding:8px 11px;margin-top:11px;line-height:1.45;text-wrap:pretty;' : 'display:none;',
    fpAccessFoot: 'Linkage records are LIVE. Invites, verification and scope changes apply in this view only until the consent service is wired: and a scope change is always the resident’s, never the building’s.',
    fpIsAccess: tabKey === 'access',
    fpIsHome: tabKey === 'home',
    fpNotHome: tabKey !== 'home',
    fpBodyFlex: tabKey === 'home' ? '0 0 0px' : '1 1 auto',
    fpGoHome: set({ tab: 'home' }),
    fpGoMessages: set({ tab: 'messages' }),
    fpBackLabel: 'All sections',
    fpPageTitle: ({ access: role === 'Operator' ? 'Coverage' : 'People & access', today: 'Today', visits: 'Visits', appt: 'Doctor visits', plan: 'Care plan', messages: 'Messages', health: 'Health', moments: 'Moments', dates: 'Dates to remember', tasks: 'To do' } as Record<string, string>)[tabKey] || '',
    // V9 labels (design fpHomeStats): the whole words, not the short forms.
    fpHomeStats: ([['Visits in the last 30 days', 'Family visits, last 30 days', M.stats.visits30d], ['Care check-ins this week', 'Care check-ins this week', M.stats.checkInsWeek], ['Requests still open', 'Requests still open', M.stats.openRequests]] as const)
      .map((x) => ({ k: x[0], full: x[1], v: x[2] == null ? DASH : String(x[2]) })),
    fpLiveLabel: 'LINKED',
    fpGoToday: set({ tab: 'today' }),
  });
  if (!R) return out;

  // ── messages, tasks, since you last looked (design L39583) ──
  const careLast = M.thread.filter((m) => !m.mine).slice(-1)[0] ?? null;
  // The family reads the care team's last word; the care team (staff view) reads the
  // thread's latest message, whoever wrote it. Before, staff saw an empty quote while a
  // family message waited for them.
  const latest = M.thread.slice(-1)[0] ?? null;
  // A family member's own message newer than the care team's reply shows as waiting, so
  // a message just sent never reads "No messages yet".
  const waiting = fam && latest?.mine ? latest : null;
  const cardMsg = fam ? careLast : latest;
  const T: Record<string, [string, string, string, string, string]> = {
    care: waiting
      ? ['Care team', 'You: ' + waiting.body, (waiting.meta ? waiting.meta + ' · ' : '') + 'Waiting for the care team', M.threadSeen, 'Reply to the care team']
      : ['Care team', cardMsg?.body ?? 'No messages yet', cardMsg?.meta ?? '', M.threadSeen, 'Reply to the care team'],
  };
  for (const x of M.threadsExtra) T[x.key] = [x.label, x.last, x.meta, x.seenBy, x.placeholder];
  const threadKeys = (['care', 'desk', 'family'] as const).filter((k) => T[k]);
  const cur = T[ui.threadKey] ?? T.care;
  const tasks = M.tasks;
  const firstNotice = M.notices[0] ?? null;
  const firstVisit = M.visits[0] ?? null;
  const attended = M.coming.filter((c) => c.state === 'ATTENDED');
  const since: Array<{ t: string; src: string; go: (e: Ev) => void }> = [];
  if (firstNotice) since.push({ t: firstNotice.k + ', ' + firstNotice.when + '.', src: 'Notice', go: set({ tab: 'today' }) });
  if (careLast) since.push({ t: 'The care team wrote: “' + careLast.body.slice(0, 60) + (careLast.body.length > 60 ? '…' : '') + '”', src: 'Message', go: set({ tab: 'messages' }) });
  if (firstVisit) since.push({ t: firstVisit.who + ' visited ' + firstVisit.when + ': ' + firstVisit.note.toLowerCase() + '.', src: 'Visit', go: set({ tab: 'visits' }) });
  if (attended.length) since.push({ t: 'Joined ' + attended.map((c) => c.what.toLowerCase().replace(', dining room', ' in the dining room')).join(' and ') + ' today.', src: 'Coming up', go: set({ tab: 'home' }) });
  Object.assign(out, {
    fpThreads: threadKeys.map((k) => ({
      label: T[k][0],
      onClick: set({ threadKey: k }),
      style: 'display:inline-flex;align-items:center;height:24px;padding:0 9px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (k === ui.threadKey ? 'background:#22272E;color:#FCFAF4;' : 'color:#3C454F;background:rgba(34,39,46,.07);'),
    })),
    // An empty thread's line is not a quotation: no quote marks around "No messages yet".
    fpHomeMsg: cur[1], fpHomeMsgQuote: !!cur[1] && !/^No messages/.test(cur[1]), fpHomeMsgMeta: cur[2], fpHomeSeenBy: cur[3] ? '✓ ' + cur[3] : '', fpReplyPh: cur[4],
    fpHomePlanReview: M.planReview,
    fpAskReview: askSend('We would like to join ' + first + '’s care-plan review' + (M.planReview ? ' on ' + M.planReview.replace(/^Next plan review: /, '') : '') + '. What time, and can one of us dial in?'),
    fpGoTasks: set({ tab: 'tasks' }),
    fpOnLifeStory: (e: Ev) => { pre(e); act.lifeStory(); },
    fpCircle: out.fpPeopleRows,
    fpIsTasks: tabKey === 'tasks',
    // V4: the family's To do; staff readers see whether each notice reached the family.
    fpIsTasksFamily: tabKey === 'tasks' && fam, fpIsTasksStaff: tabKey === 'tasks' && !fam,
    ...(fam ? fpTodoVals(M, ui, act, first, tasks.filter((x) => !x.done).length) : { fpTasksOpen: String(tasks.filter((x) => !x.done).length) }),
    ...fpToldVals(M, ui, act),
    fpTaskRows: tasks.map((x) => ({
      t: x.t,
      who: x.done ? (x.mine ? 'You did this' : 'Done by ' + x.who) : x.who ? (x.mine ? 'You’re on it' : x.who + ' is on it') : 'Nobody yet',
      whoColor: x.done ? '#1F6B52' : x.who ? '#264C7E' : '#5B6B7C',
      canClaim: !x.done && !x.who,
      canDone: !x.done && x.mine,
      onClaim: (e: Ev) => { pre(e); act.taskAct(x.id, 'claim'); },
      onDone: (e: Ev) => { pre(e); act.taskAct(x.id, 'done'); },
    })),
    fpSince: since,
    fpGoMoments: set({ tab: 'moments' }),
    fpGoDates: set({ tab: 'dates' }),
    fpIsMoments: tabKey === 'moments',
    fpIsDates: tabKey === 'dates',
  });

  // ── moments, dates, coming up (design L39622) ──
  const remPill = (x: boolean) => 'display:inline-flex;align-items:center;height:28px;padding:0 10px;border-radius:8px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;flex:0 0 auto;' +
    (x ? 'background:rgba(31,107,82,.12);color:#1F6B52;border:1px solid rgba(31,107,82,.3);' : 'color:#264C7E;border:1px solid rgba(47,92,151,.4);');
  const D = M.dates.map((d) => ({
    mon: d.mon, day: d.day, what: d.what, inDays: d.inDays, note: d.note,
    remindLabel: d.remindResident ? '✓ ' + first + ' reminded' : 'Remind ' + first, remindStyle: remPill(d.remindResident),
    onRemind: (e: Ev) => { pre(e); act.remind(d.id, { remindResident: !d.remindResident }); },
    meLabel: d.remindMe ? '✓ Reminding me' : 'Remind me', meStyle: remPill(d.remindMe),
    onMe: (e: Ev) => { pre(e); act.remind(d.id, { remindMe: !d.remindMe }); },
  }));
  Object.assign(out, {
    fpMomentGallery: M.moments,
    fpMomentsTop: M.moments.slice(0, 3),
    fpOnPhoto: (e: { target: { files?: FileList | null } }) => { const f = e?.target?.files?.[0]; if (f) act.photo(f); },
    fpPhotoNote: M.photoSent ? '✓ On ' + first + '’s Atlas screen now' : M.photoNote ?? 'Shows on ' + first + '’s Atlas screen',
    fpPhotoNoteColor: M.photoSent ? '#1F6B52' : M.photoNote ? '#8A5A00' : '#54606C',
    fpComing: M.coming.map((c) => {
      const canCal = c.state !== 'ATTENDED';
      const ics = 'BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:' + first + ': ' + c.what + '\nDTSTART:' + c.at.replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z') + '\nDESCRIPTION:' + R.facility + '\nEND:VEVENT\nEND:VCALENDAR';
      return {
        when: c.when, what: c.what, state: c.state,
        stateColor: c.state === 'ATTENDED' ? '#1F6B52' : c.state === 'TODAY' ? '#2F5C97' : '#54606C',
        canCal, ics: 'data:text/calendar;charset=utf-8,' + encodeURIComponent(ics),
        file: first.toLowerCase() + '-' + c.what.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.ics',
      };
    }),
    fpDates: D,
    fpDatesTop: D.slice(0, 3),
    fpWeeklyToggle: (e: Ev) => { pre(e); act.weekly(!M.weekly); },
    fpWeeklyLabel: M.weekly ? '✓ Emailing you this every Sunday' : 'Email me this every Sunday',
    fpWeeklyStyle: 'align-self:flex-start;margin-top:auto;display:inline-flex;align-items:center;height:30px;padding:0 11px;border-radius:8px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (M.weekly ? 'background:rgba(31,107,82,.12);color:#1F6B52;border:1px solid rgba(31,107,82,.3);' : 'color:#264C7E;border:1px solid rgba(47,92,151,.4);'),
    fpGoHealth: set({ tab: 'health' }), fpGoVisits: set({ tab: 'visits' }), fpGoPlan: set({ tab: 'plan' }), fpGoAppt: set({ tab: 'appt' }), fpGoAccess: set({ tab: 'access' }),
  });

  // ── home panels (design L39679) ──
  const falls = M.notices.filter((n) => /fall/i.test(n.k));
  const f0 = falls[0] ?? null;
  const spark = M.moodSeries.length > 1
    ? (() => { const lo = Math.min(...M.moodSeries) - 0.1, hi = Math.max(...M.moodSeries) + 0.1; return M.moodSeries.map((v, i) => ((i * 120) / (M.moodSeries.length - 1)).toFixed(1) + ',' + (32 - ((v - lo) / (hi - lo)) * 30).toFixed(1)).join(' '); })()
    : '';
  Object.assign(out, {
    fpHomeNotices: M.notices.slice(0, 3).map((n) => {
      const seen = !!ui.eventSeen['n:' + n.id] || n.mine;
      return {
        k: n.k, when: n.when, told: n.told, by: n.by,
        // Every role gets "Mark seen" (the design's rule): it records the viewer's own receipt.
        isSeen: seen, notSeen: !seen, isMed: n.med,
        onAsk: askSend('About the change on ' + n.when + ' (' + n.k.toLowerCase() + '): what should we watch for, and can ' + first + ' explain it back?'),
        seenBy: 'Seen by: ' + n.seenBy.map((c) => c.name + (c.seen ? ' ✓' : ' –')).join(' · '),
        pulse: seen ? 'none' : 'fpNew 2.2s ease-in-out infinite',
        onSeen: (e: Ev) => { pre(e); act.noticeSeen(n); },
        dot: n.sev === 'high' ? '#B3261E' : n.sev === 'med' ? '#3C454F' : '#54606C',
      };
    }),
    fpHomeMood: band[0], fpHomeMoodColor: band[1],
    fpHomeSpark: spark,
    fpHomeFallTier: tierArr[0], fpHomeFallColor: tierArr[1],
    fpHomeFallTold: f0 ? f0.told + ' · ' + f0.by + ' · ' + f0.cls : '',
    fpHomeLastFall: f0 ? 'Last: ' + f0.k.toLowerCase() + ', ' + f0.when : 'No falls on record',
    fpHomeVisits: M.visits.slice(0, 4).map((v) => ({
      // Home speaks as the family for every role (the design's literal rows); the Visits tab names them for staff.
      who: v.who, when: v.when, note: v.note, ago: v.days === 0 ? 'today' : v.days + 'd ago',
      w: Math.max(8, 100 - Math.min(100, v.days * 5)) + '%', c: v.days <= 7 ? '#1F6B52' : v.days <= 14 ? '#2F5C97' : '#3C454F',
    })),
    // No goals shared is said, never "0 of 0 goals on track".
    fpHomePlanOn: M.plan.length ? String(M.plan.filter((g) => g.on).length) : '',
    fpHomePlanOf: M.plan.length ? 'of ' + M.plan.length + ' goals on track' : 'No care-plan goals shared yet',
    fpHomePlan: M.plan.map((g) => ({ goal: g.goal, mark: g.on ? '✓' : '•', c: g.on ? '#1F6B52' : '#3C454F', off: !g.on, onAsk: askSend('How is this goal going: “' + g.goal + '”?') })),
    fpHomeMeds: M.meds.map((m) => ({ name: m.name, rule: m.rule, onAsk: askSend('Could we talk about ' + m.name + '? The portal flagged it: ' + m.rule.toLowerCase() + '.') })),
    fpAskMood: askSend('How has ' + first + ' been feeling this week? Anything we should know before we call?'),
    fpAskFalls: askSend('What is being done to lower ' + first + '’s fall risk, and is there anything the family can help with?'),
    fpHomeSentShow: ui.sent.length > 0,
    fpHomeSent: ui.sent.length
      ? '✓ Sent to ' + ({ care: 'the care team', desk: 'the front desk', family: 'the family circle' } as Record<string, string>)[ui.sent[ui.sent.length - 1].to || 'care'] + ': “' +
        ui.sent[ui.sent.length - 1].body.slice(0, 70) + (ui.sent[ui.sent.length - 1].body.length > 70 ? '…' : '') + '”'
      : '',
    fpHomeCircleLabel: role === 'Operator' ? 'Coverage' : 'Family circle',
    fpHomeCircle: M.circle.map((c) => ({ who: c.who, role: c.role, last: c.lastShort, ini: c.who.split(/[\s·]+/)[0].slice(0, 2).toUpperCase() })),
  });

  // ── doctor-visit reconciliation (design L39745) ──
  const tone: Record<string, string> = { MISMATCH: '#B3261E', PARTIAL: '#3C454F', MATCH: '#1F6B52' };
  const ownerOf = (o: string) => (o === 'you' ? (fam ? 'you' : M.self.name) : o);
  const items = M.appt?.items ?? [];
  const mis = items.filter((r) => r.verdict !== 'MATCH').length;
  Object.assign(out, {
    fpApptHead: fam ? 'Her appointment on Tuesday, and what she took away from it' : 'Doctor-visit reconciliation · what was said against what was heard',
    fpApptSub: fam
      ? (items.length ? ['Nothing', 'One thing', 'Two things', 'Three things', 'Four things', 'Five things'][items.length] ?? items.length + ' things' : 'Nothing') + ' ' + (items.length === 1 ? 'was' : 'were') + ' said. She recalled ' + (['none', 'one', 'two', 'three', 'four', 'five'][mis] ?? String(mis)) + ' of them differently' +
        (items.some((r) => r.verdict === 'MISMATCH') ? ', and one of those differences could change whether she takes a tablet tonight.' : '.')
      : mis + ' of ' + items.length + ' items diverge. A mismatch on a medication instruction is the highest-value thing this console surfaces: it is caught before the next pass, not at the next appointment.',
    fpApptChain: (M.appt?.chain ?? []).map((c, i) => ({
      label: c.label, sub: c.sub,
      dot: 'width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:#1F6B52;animation:riseIn .3s ease-out;animation-delay:' + i * 70 + 'ms;',
      style: 'display:flex;align-items:flex-start;gap:8px;min-width:0;padding:7px 0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 70 + 'ms;',
    })),
    fpApptRows: items.map((r, i) => {
      const t = tone[r.verdict] ?? '#3C454F';
      const done = r.done || !!ui.apptDone[i];
      return {
        said: '“' + r.said + '”', heard: '“' + r.heard + '”', verdict: r.verdict, why: r.why,
        vStyle: 'display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;white-space:nowrap;color:' + t + ';background:' +
          (r.verdict === 'MISMATCH' ? 'rgba(179,38,30,.12)' : r.verdict === 'PARTIAL' ? 'rgba(34,39,46,.13)' : 'rgba(47,138,111,.12)') + ';',
        cardStyle: 'border:1px solid ' + (r.verdict === 'MISMATCH' ? 'rgba(179,38,30,.28)' : 'rgba(34,39,46,.13)') + ';border-radius:12px;background:#FCFAF4;padding:12px 14px;min-width:0;' +
          'animation:riseIn .32s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 55 + 'ms;' + (done ? 'opacity:.72;' : ''),
        hasGap: r.verdict !== 'MATCH',
        action: r.action, owner: ownerOf(r.owner),
        canAct: fam ? r.owner === 'you' : r.owner !== 'you',
        isWatch: fam ? r.owner !== 'you' : r.owner === 'you',
        actLabel: 'Done',
        actStyle: 'display:inline-flex;align-items:center;height:25px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
          (done ? 'background:rgba(47,138,111,.13);color:#1F6B52;pointer-events:none;' : 'background:#2F5C97;color:#fff;'),
        watchLabel: done ? 'Done' : 'Waiting on ' + ownerOf(r.owner),
        watchStyle: 'display:inline-flex;align-items:center;height:25px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;white-space:nowrap;' +
          (done ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#3C454F;background:rgba(34,39,46,.12);'),
        onAct: (e: Ev) => { pre(e); if (!done) act.apptDone(i); },
      };
    }),
    fpApptFoot: 'The recording and its summary are sealed. Her recollection is asked in her own words afterwards: we never correct her in the moment, and we never merge the two records into one “what happened”.',
  });

  // ── visits (design L39818) ──
  const daysAsc = M.visits.map((v) => v.days).slice().sort((p, q) => p - q);
  const gaps: number[] = [];
  for (let i = 1; i < daysAsc.length; i++) gaps.push(daysAsc[i] - daysAsc[i - 1]);
  const bandDays = gaps.length ? Math.round(gaps.reduce((t, g) => t + g, 0) / gaps.length) : 0;
  const last = daysAsc[0] ?? null;
  const newestFor: Record<string, number> = {};
  M.visits.forEach((v) => { const k = v.mine ? '__me' : v.who; if (newestFor[k] === undefined || v.days < newestFor[k]) newestFor[k] = v.days; });
  const visLabel = (v: FpVisit) => (v.mine ? (fam ? 'You' : M.self.name + ' (' + M.self.rel + ')') : v.who);
  Object.assign(out, {
    fpVisFam: fam,
    fpVisHead: fam ? (last == null ? 'No visit on record yet' : 'Her last visit was ' + last + (last === 1 ? ' day ago' : ' days ago')) : 'Visit pattern, and who has drifted',
    fpVisSub: fam
      ? 'Her visits land about ' + bandDays + ' days apart, counting the gaps between them. This is not a scoreboard: it is here because company is the one thing the building cannot provide for her, and she is never shown a count.'
      : 'The same isolation trajectory Guardian scores, said to the family without blame. A drift is a conversation to open, never a metric to enforce.',
    fpVisRows: M.visits.map((v, i) => {
      const isNewest = newestFor[v.mine ? '__me' : v.who] === v.days;
      const drift = isNewest && v.days > bandDays * 1.6;
      const t = !isNewest ? '#54606C' : drift ? '#3C454F' : v.days > bandDays ? '#3C454F' : '#1F6B52';
      return {
        who: visLabel(v), last: v.when + ' · ' + v.note, days: v.days + (v.days === 1 ? ' day ago' : ' days ago'),
        daysStyle: 'font-size:16px;font-weight:700;white-space:nowrap;font-variant-numeric:tabular-nums;color:' + t + ';',
        track: 'display:block;height:5px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;margin-top:5px;',
        bar: 'display:block;height:100%;border-radius:2px;transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:' + i * 60 + 'ms;width:' +
          Math.min(100, Math.round((v.days / Math.max(1, bandDays * 4)) * 100)) + '%;background:' + t + ';',
        note: !isNewest ? 'Earlier visit' : drift ? 'Longer than her own interval' : v.days > bandDays ? 'Longer than her usual gap' : 'Within her usual gap',
        rowStyle: 'display:grid;grid-template-columns:minmax(0,1fr) minmax(130px,190px);gap:12px;align-items:center;padding:9px 0;border-top:1px solid rgba(34,39,46,.07);min-width:0;' +
          'animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 50 + 'ms;' + (isNewest ? '' : 'opacity:.7;'),
      };
    }),
    fpVisNudgeShow: fam,
    fpVisActions: fam
      ? [['Book a visit', 'We will have her ready and tell the floor you are coming.'], ['Ask us to arrange a call', 'A staff member sits with her and dials you.'], ['Send a photo to her screen', 'It shows on Atlas and she can talk about it.']].map((x, i) => ({
        label: x[0], sub: x[1],
        style: 'display:block;text-decoration:none;border:1px solid rgba(34,39,46,.13);border-radius:10px;background:#FCFAF4;padding:11px 13px;min-width:0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 50 + 'ms;',
        onClick: (e: Ev) => { pre(e); act.familyRequest(x[0], x[0], () => act.setUi({ visitAsk: x[0] + ' requested. It is now in the care team’s queue as a family request: they will confirm the time with you.' })); },
      }))
      : [],
    fpVisitAsk: ui.visitAsk,
    fpVisitAskStyle: ui.visitAsk ? 'font-size:14px;font-weight:600;color:#2F5C97;background:rgba(79,138,214,.10);border-radius:8px;padding:9px 12px;margin-top:11px;line-height:1.45;text-wrap:pretty;' : 'display:none;',
    fpVisFoot: fam
      ? 'Visit records come from the front-desk log. Requests arrive to staff labeled family request, never as a safety alert. Shown so the family can spread visits out rather than overlap; no part of her care depends on it.'
      : 'Isolation is scored against each resident’s own visit pattern, never a facility average. A resident with no representative on file cannot drift: there is nobody to drift.',
    fpVisitNudge: 'Nobody has a visit planned this week. Her afternoons are quietest, and staff shifts change at 3pm, so early afternoon is also the easiest time to catch her nurse.',
  });

  // ── Today: events and the eight panels (design L39878, L39933) ──
  const canAck = fam;
  Object.assign(out, {
    fpEvHead: fam ? (['Nothing', 'One thing', 'Two things', 'Three things', 'Four things', 'Five things'][M.events.length] ?? M.events.length + ' things') + ' we told you' : 'What the family has been told, and whether they saw it',
    fpEvSub: fam
      ? 'Every notice says how we know, what we did about it, and what we are still watching for. Nothing here is a guess dressed as a fact.'
      : 'The 483.10(g)(14) notice record, family-facing. An unacknowledged urgent notice is a phone call somebody still owes.',
    fpEvents: M.events.map((e, i) => {
      const ack = e.acknowledged || !!ui.eventSeen['e:' + e.id];
      const urgent = e.urgent;
      return {
        kind: e.kind, head: e.head, when: e.when, how: e.how, did: e.did, watch: e.watch, owner: e.owner,
        tagStyle: 'display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;white-space:nowrap;' +
          (urgent ? 'color:#B3261E;background:rgba(179,38,30,.12);' : 'color:#2F5C97;background:rgba(79,138,214,.12);'),
        spine: 'display:block;width:3px;border-radius:2px;flex:0 0 3px;align-self:stretch;background:' + (urgent ? '#B3261E' : '#2F5C97') + ';' + (urgent && !ack ? 'animation:pulseSpine 2.4s ease-in-out infinite;' : ''),
        cardStyle: 'display:flex;gap:12px;border:1px solid ' + (urgent && !ack ? 'rgba(179,38,30,.3)' : 'rgba(34,39,46,.13)') + ';border-radius:12px;background:#FCFAF4;padding:12px 14px;min-width:0;' +
          'animation:riseIn .32s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 55 + 'ms;' + (ack ? 'opacity:.72;' : ''),
        canAck, isStaffView: !canAck,
        ackLabel: ack ? 'Seen' : urgent ? 'I have read this' : 'Mark seen',
        ackStyle: 'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
          (ack ? 'background:rgba(47,138,111,.13);color:#1F6B52;pointer-events:none;' : urgent ? 'background:#B3261E;color:#fff;' : 'background:rgba(34,39,46,.05);color:#3C454F;border:1px solid rgba(34,39,46,.14);'),
        onAck: (ev: Ev) => { pre(ev); if (!ack) act.eventAck(e.id); },
        stateLabel: ack ? 'Family acknowledged' : urgent ? 'Not yet acknowledged · somebody owes a call' : 'Not yet acknowledged',
        stateStyle: 'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;white-space:nowrap;' +
          (ack ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : urgent ? 'color:#B3261E;background:rgba(179,38,30,.11);' : 'color:#3C454F;background:rgba(34,39,46,.12);'),
      };
    }),
    fpEvFoot: 'Notice records are LIVE. Acknowledgements apply in this view only until the consent service is wired. A watch window is a period a person is looking: not a prediction that something will happen.',
  });
  const pTone = (basis: string, trend: string) => (basis === 'NOT OBSERVED' || basis === 'PRIVATE' ? '#54606C' : trend === 'down' ? '#3C454F' : trend === 'up' ? '#1F6B52' : '#3C454F');
  // "worth your attention": the panels whose next move is the family's own
  const attention = M.panels.filter((p) => p.owner === 'you').length;
  const words = ['None', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight'];
  Object.assign(out, {
    fpStatusHead: fam ? first + ' is having ' + (M.panels[0]?.value?.match(/an? (.*?) day/)?.[0] ?? 'an ordinary day') : 'What her family sees right now',
    fpStatusSub: fam
      ? (words[M.panels.length] ?? M.panels.length) + ' things we watch. ' + (words[attention] ?? attention) + (attention === 1 ? ' is' : ' are') + ' worth your attention this week; the rest are steady against her own normal, not against anybody else’s.'
      : 'The same ' + (words[M.panels.length] ?? M.panels.length).toLowerCase() + ' panels the linked family is looking at, so nobody is surprised by a conversation. A panel with no observation says so rather than showing a number.',
    fpPanels: M.panels.map((p, i) => {
      const t = pTone(p.basis, p.trend);
      const acked = !!ui.panelAck[p.label];
      const nothing = p.action.indexOf('Nothing') === 0;
      return {
        // Not shared is her choice, not a missing reading: no tag at all.
        label: p.label, value: p.value, basis: p.basis === 'PRIVATE' ? '' : p.basis, why: p.why, action: p.action, owner: p.owner,
        notMood: p.label !== 'Mood', hasAction: p.action !== 'Nothing to do',
        onAsk: askSend('About ' + p.label.toLowerCase() + ': the portal says “' + p.value + '”. What are you seeing, and is there anything the family should do?'),
        arrow: p.trend === 'down' ? '↓' : p.trend === 'up' ? '↑' : p.trend === 'flat' ? '→' : '',
        valStyle: 'display:block;font-size:16px;font-weight:700;line-height:1.35;margin-top:5px;text-wrap:pretty;color:' + t + ';',
        basisStyle: 'display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;white-space:nowrap;' +
          (p.basis === 'MEASURED' ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : p.basis === 'MODELED' ? 'color:#3C454F;background:rgba(34,39,46,.13);' : 'color:#54606C;background:rgba(34,39,46,.07);'),
        spark: p.basis === 'NOT OBSERVED' || p.basis === 'PRIVATE' ? [] : fpBars(p.series, t),
        hasSpark: p.basis !== 'NOT OBSERVED' && p.basis !== 'PRIVATE',
        sparkNote: p.basis === 'PRIVATE' ? '' : p.basis === 'NOT OBSERVED' ? 'Nothing to plot, and we will not infer it from something else.' : '14 days · grey is inside her own normal range',
        bandStyle: 'position:absolute;left:0;right:0;bottom:40%;height:32%;background:rgba(34,39,46,.05);border-top:1px dashed rgba(34,39,46,.14);border-bottom:1px dashed rgba(34,39,46,.14);pointer-events:none;',
        cardStyle: 'border:1px solid rgba(34,39,46,.13);border-radius:12px;background:#FCFAF4;padding:12px 14px;min-width:0;animation:riseIn .32s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 45 + 'ms;',
        actStyle: 'font-size:14px;line-height:1.45;margin-top:7px;text-wrap:pretty;color:' + (nothing ? '#54606C' : '#22272E') + ';font-weight:' + (nothing ? '500' : '700') + ';',
        needsAck: !nothing && fam,
        showState: !nothing && !fam,
        stateLabel: acked ? 'Family marked seen' : 'Family has not marked this seen',
        stateStyle: 'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;white-space:nowrap;margin-top:8px;' +
          (acked ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#3C454F;background:rgba(34,39,46,.12);'),
        ackLabel: acked ? 'Seen' : 'Mark seen',
        ackStyle: 'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;margin-top:8px;' +
          (acked ? 'background:rgba(47,138,111,.13);color:#1F6B52;pointer-events:none;' : 'background:rgba(34,39,46,.05);color:#3C454F;border:1px solid rgba(34,39,46,.14);'),
        onAck: (e: Ev) => { pre(e); if (!acked) act.panelSeen(p.label); },
      };
    }),
    fpPanelFoot: 'MEASURED means a person or a device recorded it. MODELED means we inferred it from her own pattern and it carries no accuracy figure yet. A panel we cannot observe stays grey: unknown is never green.',
    fpIsToday: tabKey === 'today',
    fpIsVisits: tabKey === 'visits',
    fpIsAppt: tabKey === 'appt',
    fpIsPlan: tabKey === 'plan',
    fpNotices: M.notices.map((n) => {
      const seen = !!ui.eventSeen['n:' + n.id] || n.mine;
      return {
        k: n.k, when: n.when, told: n.told, by: n.by, cls: n.cls,
        ackLabel: seen ? 'Acknowledged' : 'Mark as read',
        ackStyle: 'display:inline-flex;align-items:center;height:27px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;border:1px solid ;white-space:nowrap;' +
          (seen ? 'rgba(47,138,111,.3)' : 'rgba(34,39,46,.16)') + ';' + (seen ? 'background:rgba(47,138,111,.11);color:#1F6B52;' : 'background:#FCFAF4;color:#54606C;'),
        onAck: (e: Ev) => { pre(e); if (!seen) act.noticeSeen(n); },
        dotStyle: 'display:inline-block;width:7px;height:7px;flex:0 0 7px;border-radius:50%;background:' + (n.sev === 'high' ? '#B3261E' : n.sev === 'med' ? '#3C454F' : '#54606C') + ';',
      };
    }),
    fpNoticeLead: M.notices.length + ' notices in the last 30 days · ' + M.notices.filter((n) => !(n.mine || ui.eventSeen['n:' + n.id])).length + ' not yet marked read',
    fpNoticeNote: 'Federal rule 483.10(g)(14) requires the facility to tell you about accidents and injuries, significant changes in her condition, transfers and hospitalisations. This is the record of those notices, including when and how you were told.',
    fpOnboard: M.firstMonth,
    fpCircleNote: 'Everyone here sees the same updates, so you are not each phoning the nurse separately. Only the approver can change access.',
  });

  // ── care plan (design L40029) ──
  Object.assign(out, {
    fpPlanRows: M.plan.map((p, i) => {
      const t = p.pct >= 70 ? '#1F6B52' : p.pct > 0 ? '#3C454F' : '#54606C';
      const done = !!ui.planRaised[i];
      const own = p.owner === 'you' ? (fam ? 'you' : M.self.name) : p.owner;
      return {
        goal: p.goal, how: p.how,
        state: p.on ? 'On track' : 'Not happening yet',
        stateStyle: 'display:inline-flex;align-items:center;font-size:14px;font-weight:700;border-radius:5px;padding:2px 8px;flex:0 0 auto;color:' + (p.on ? '#1F6B52' : '#3C454F') + ';background:' + (p.on ? 'rgba(47,138,111,.13)' : 'rgba(34,39,46,.14)') + ';',
        evidence: p.evidence, basis: p.basis, sourceNote: 'from the ' + p.source,
        basisStyle: 'display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;white-space:nowrap;' +
          (p.basis === 'MEASURED' ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#54606C;background:rgba(34,39,46,.07);'),
        track: 'display:block;height:5px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;margin-top:6px;max-width:260px;',
        bar: 'display:block;height:100%;border-radius:2px;transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:' + i * 55 + 'ms;width:' + Math.max(3, p.pct) + '%;background:' + t + ';',
        hasNext: !!p.next,
        next: p.next, owner: own,
        canAct: !!p.next && (fam ? p.owner === 'you' : p.owner !== 'you'),
        isWatch: !!p.next && (fam ? p.owner !== 'you' : p.owner === 'you'),
        actLabel: done ? 'Raised' : 'Raise this',
        actStyle: 'display:inline-flex;align-items:center;height:25px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
          (done ? 'background:rgba(47,138,111,.13);color:#1F6B52;pointer-events:none;' : 'background:#2F5C97;color:#fff;'),
        watchLabel: done ? 'Raised' : 'With ' + own,
        watchStyle: 'display:inline-flex;align-items:center;height:25px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;white-space:nowrap;' +
          (done ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#3C454F;background:rgba(34,39,46,.12);'),
        onAct: (e: Ev) => { pre(e); if (!done) { act.familyRequest('care_plan', 'Raise at the care conference: ' + p.goal, () => act.setUi({ planRaised: { ...ui.planRaised, [i]: true } })); } },
        rowStyle: 'border-top:1px solid rgba(34,39,46,.08);padding-top:11px;min-width:0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 50 + 'ms;',
      };
    }),
    fpPlanEvFoot: 'Each goal is measured against its own target, not a facility average. A goal with nothing recorded stays grey rather than being called met.',
    fpPlanLead: 'Her care plan, in plain words. You are part of the team that writes it, and you can ask for it to change.',
    fpPlanMeeting: M.planMeeting,
    fpPlanGap: M.planGap,
    fpPlanAsk: ui.askedMeeting ? 'Request sent. The social worker will call you to set a time, usually within two working days.' : '',
    onFpAskMeeting: (e: Ev) => { pre(e); if (!ui.askedMeeting) act.meeting(); },
    fpAskStyle: 'display:inline-flex;align-items:center;justify-content:center;height:26px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' +
      (ui.askedMeeting ? 'background:rgba(47,138,111,.13);color:#1F6B52;' : 'background:#2F5C97;color:#fff;'),
    fpAskLabel: ui.askedMeeting ? 'Meeting requested' : 'Ask for a care conference',
    fpPlanNote: 'This is the plan the facility is required to follow. If something on it is not happening, saying so at a conference is the strongest thing you can do.',
    fpSummaryBuilt: ui.summaryOpen,
    onFpSummary: (e: Ev) => { pre(e); act.setUi({ summaryOpen: !ui.summaryOpen }); },
    fpSummaryLabel: ui.summaryOpen ? 'Hide the summary' : 'Prepare a summary for her doctor',
    fpSummaryStyle: 'display:inline-flex;align-items:center;justify-content:center;height:26px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;border:1px solid rgba(47,92,151,.3);background:rgba(47,92,151,.07);color:#2F5C97;white-space:nowrap;',
    fpSummaryLines: M.summaryLines,
    fpSummaryFor: 'For ' + M.doctor + ' · ' + R.name,
    fpSummaryNote: 'Written for a 15-minute appointment: what changed, over what period, and what to ask. It carries no diagnosis and no recommendation.',
  });

  // ── messages (design L40094) ──
  const quick: Array<[string, string]> = [
    ['Ask about her appetite', 'Two dinners were left this week: ask what staff are seeing.'],
    ['Ask for a call back', 'A nurse rings you at a time you choose.'],
    ['Request a ride', 'Goes to the care team as a family request, not an alert.'],
  ];
  Object.assign(out, {
    fpMsgFam: fam,
    fpMsgStaff: !fam,
    fpQuick: fam
      ? quick.map((q, i) => {
        const used = !!ui.quickUsed[q[0]];
        return {
          label: q[0], sub: q[1], done: used,
          style: 'display:block;text-decoration:none;border:1px solid ' + (used ? 'rgba(47,138,111,.35)' : 'rgba(34,39,46,.13)') + ';border-radius:10px;background:#FCFAF4;padding:10px 12px;min-width:0;' +
            'animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 45 + 'ms;' + (used ? 'opacity:.72;' : ''),
          labelStyle: 'display:block;font-size:16px;font-weight:700;color:' + (used ? '#1F6B52' : '#2F5C97') + ';',
          onClick: (e: Ev) => { pre(e); if (!used) { act.familyRequest(q[0], q[0], () => act.setUi({ quickUsed: { ...ui.quickUsed, [q[0]]: true } })); } },
        };
      })
      : [],
    fpQuickNote: fam ? 'These route as family requests with a named owner and a clock: they do not sit in a thread hoping somebody reads it.' : '',
    fpMsgState: !fam && M.inbox
      ? [['Open from family', String(M.inbox.open), 'awaiting a reply from the floor', '#3C454F'], ['Replied within SLA', M.inbox.sla, 'target is one working day', '#1F6B52'], ['Routed as requests', M.inbox.routed, 'became care requests, not thread replies', '#3C454F']]
        .map((x) => ({ k: x[0], v: x[1], foot: x[2], vStyle: 'font-weight:800;letter-spacing:-.03em;margin-top:4px;line-height:1;font-variant-numeric:tabular-nums;font-size:22px;color:' + x[3] + ';' }))
      : [],
    fpIsMsg: tabKey === 'messages',
  });

  // ── health (design L40128, L40203) ──
  const act0 = M.healthAction;
  Object.assign(out, {
    fpHealthSpark: fpBars(M.healthSeries, '#3C454F'),
    fpHealthSparkNote: M.healthNote,
    fpHealthBasis: M.healthBasis.map((x, i) => ({
      label: x.label, note: x.note, basis: x.basis,
      style: 'display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:3px;padding:7px 0;border-top:1px solid rgba(34,39,46,.07);min-width:0;animation:riseIn .28s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 40 + 'ms;',
      basisStyle: 'display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;white-space:nowrap;' +
        (x.basis === 'MEASURED' ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#3C454F;background:rgba(34,39,46,.13);'),
    })),
    fpHealthAction: act0 ? (fam ? act0.family : act0.staff) : 'Nothing is booked yet.',
    fpHealthOwner: act0 ? (fam ? act0.ownerFamily : act0.ownerStaff) : DASH,
    fpHealthGrid: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:12px;margin-top:12px;align-items:start;min-width:0;',
    fpIsHealth: tabKey === 'health',
    fpName: R.name,
    fpInitials: R.initials,
    fpMeta: 'Room ' + R.room + ' · ' + R.facility + // The header line is the design's literal for every role.
      (M.self.rel ? ' · you are listed as ' + relWords(M.self.rel) : ''),
    fpPill: band[0],
    fpPillStyle: 'display:inline-flex;align-items:center;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;border-radius:4px;padding:3px 8px;color:' + band[1] + ';background:' + band[2] + ';',
    fpSummary: M.summary,
    fpSpark: M.moodSeries.map((vv) => ({
      style: 'display:block;flex:1;min-width:3px;border-radius:2px 2px 0 0;background:rgba(47,92,151,' + (vv >= 3.9 ? '.62' : vv >= 3.4 ? '.42' : vv >= 2.8 ? '.24' : '.13') + ');height:' + Math.round((vv / 5) * 100) + '%;',
    })),
    fpSparkNote: 'Day-to-day wellbeing, last 14 days. A shorter bar is a quieter day, not an alert.',
    fpMoments: M.highlights,
    fpMomentsNote: 'Moments her care team chose to share. Her conversations themselves stay private to her.',
    fpCareNote: M.careNote,
    fpCareBy: M.careBy,
    fpVisits: ([['Family visits, last 30 days', M.stats.visits30d], ['Care check-ins this week', M.stats.checkInsWeek], ['Requests still open', M.stats.openRequests]] as const).map((x) => ({ k: x[0], v: x[1] == null ? DASH : String(x[1]) })),
    fpPrivacy: 'You see what her consent allows: wellbeing, shared moments, and notes her care team writes for you. You are told when something significant happens to her, such as a fall, an injury or a big change. You do not see the staff alert feed, raw scores, or the words she says to Atlas. She or her approver can change this at any time.',
    fpMsgTitle: 'Care team · ' + R.facility,
    fpMsgSla: 'Answered on shift, usually within a few hours',
    fpThread: M.thread.concat(ui.sent.filter((m) => !m.to || m.to === 'care').map((m) => ({ mine: true, body: m.body, meta: 'You · just now' }))).map((m) => ({
      body: m.body,
      meta: fam ? m.meta : m.meta.replace(/^You\b/, M.self.name),
      rowStyle: 'display:flex;flex-direction:column;gap:4px;align-items:' + (m.mine ? 'flex-end' : 'flex-start') + ';',
      bubbleStyle: 'max-width:78%;font-size:16px;line-height:1.55;border-radius:12px;padding:11px 14px;' +
        (m.mine ? 'background:rgba(47,92,151,.1);color:#22272E;border:1px solid rgba(47,92,151,.2);' : 'background:#F3EFE5;color:#22272E;border:1px solid rgba(34,39,46,.1);'),
      metaStyle: 'font-size:14px;color:#54606C;',
    })),
    fpDraft: ui.draft,
    onFpDraft: (e: { target: { value: string } }) => act.setUi({ draft: e.target.value }),
    fpMsgInputStyle: 'flex:1;min-width:0;height:38px;border:1px solid rgba(34,39,46,.2);border-radius:8px;padding:0 12px;font-size:16px;color:#22272E;background:#fff;font-family:inherit;box-sizing:border-box;',
    fpSendStyle: 'display:inline-flex;align-items:center;justify-content:center;height:38px;padding:0 18px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
      (ui.draft.trim() ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.07);color:#54606C;'),
    onFpSend: (e: Ev) => { pre(e); if (ui.draft.trim()) act.send(ui.draft.trim(), ui.threadKey); },
    fpMsgNote: 'This reaches the care team on shift, not an emergency line. For anything urgent, call the facility.',
    fpHealthDisc: 'These are wellness observations to raise with her doctor. They are not a diagnosis, not a prediction, and nothing here changes her care or her medication. A person always decides.',
    fpTier: tierArr[0],
    fpTierColor: tierArr[1],
    fpTierStyle: 'display:inline-flex;align-items:center;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;border-radius:4px;padding:2px 7px;color:' + tierArr[1] + ';background:' + tierArr[2] + ';',
    fpTierSub: 'Measured against her own recent weeks, not against other people.',
    fpScore: fs == null ? DASH : String(fs),
    fpScorePct: (fs ?? 0) + '%',
    fpConf: M.fall.conf,
    fpDrivers: M.drivers.map((d, i) => ({ label: d.w == null ? d.label + ' ' + DASH : d.label, w: d.w == null ? '0' : d.w + '%', color: 'rgba(47,92,151,' + ['.55', '.38', '.24', '.16', '.12'][i] + ')' })),
    fpTierMethod: 'Built from her own recent weeks, updated daily. It has no accuracy figure yet because it is a transparent trend, not a trained predictor. Her care team sees the same numbers you do.',
    fpMedSub: (['No items', 'One item', 'Two items', 'Three items', 'Four items'][M.meds.length] ?? M.meds.length + ' items') + ' on her list worth raising at her next appointment. Nothing has been changed.',
    fpMeds: M.meds.map((m, mi) => ({
      name: m.name, rule: m.rule, why: m.why,
      ruleStyle: 'display:inline-flex;align-items:center;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;border-radius:4px;padding:2px 7px;color:#54606C;background:rgba(34,39,46,.07);',
      outcomes: ['Discussed', 'Doctor reduced', 'Keeping it'].map((o) => ({
        label: o,
        onClick: (e: Ev) => { pre(e); act.medOutcome(mi, m.name, ui.medOut[mi] === o ? null : o); },
        style: 'display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;border:1px solid ;white-space:nowrap;' +
          (ui.medOut[mi] === o ? 'rgba(47,92,151,.32)' : 'rgba(34,39,46,.16)') + ';' + (ui.medOut[mi] === o ? 'background:rgba(47,92,151,.1);color:#2F5C97;' : 'background:#FCFAF4;color:#54606C;'),
      })),
      logged: ui.medOut[mi] ? 'You logged: ' + ui.medOut[mi] + '. This records her doctor’s decision; the app changed nothing.' : 'Log what her doctor decides, once you have asked.',
    })),
    fpMedNote: 'Her full medication list stays with her care team. This panel only surfaces items a review rule flagged, and only ever as a question.',
  });
  return out;
}

/** The family setup line (design L41315): Family only, until the server says it is complete. */
export interface FsuState {
  channel: 'sms' | 'push' | 'email' | null;
  testAckSeconds: number | null;
  testSent: boolean;
  hello: 'msg' | 'photo' | null;
  readAt: string | null;
  date: string | null;
  complete: boolean;
}
export function fsuVals(opts: {
  first: string;
  s: FsuState;
  open: boolean;
  hidden: boolean;
  act: { toggle: () => void; hide: () => void; rules: () => void; channel: (c: 'sms' | 'push' | 'email') => void; test: () => void; hello: (kind: 'msg' | 'photo') => void; date: (label: string, monthDay: string) => void; finish: () => void };
}) {
  const { s, first } = opts;
  const d1 = !!(s.channel && s.testAckSeconds != null);
  const d2 = !!s.readAt;
  const d3 = !!s.date;
  const n = [d1, d2, d3].filter(Boolean).length;
  const complete = n === 3 || s.complete;
  const next = !d1 ? 0 : !d2 ? 1 : !d3 ? 2 : -1;
  const num = (done: boolean, i: number) => 'flex:0 0 28px;display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;font-size:16px;font-weight:700;' +
    (done ? 'background:#1F6B52;color:#FCFAF4;' : i === next ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.10);color:#3C454F;');
  const btn = (primary: boolean) => 'display:inline-flex;align-items:center;min-height:48px;padding:0 18px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
    (primary ? 'color:#fff;background:#2F5C97;' : 'color:#22272E;background:rgba(34,39,46,.09);');
  const chip = (on: boolean) => 'display:inline-flex;align-items:center;min-height:48px;padding:0 16px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
    (on ? 'color:#fff;background:#22272E;' : 'color:#22272E;background:rgba(34,39,46,.07);border:1px solid rgba(34,39,46,.16);');
  const ev = (done: boolean) => 'color:' + (done ? '#1F6B52' : '#3C454F') + ';';
  const CH: Array<['sms' | 'push' | 'email', string]> = [['sms', 'Text message'], ['push', 'App notification'], ['email', 'Email']];
  const chName = s.channel ? (CH.find((c) => c[0] === s.channel)?.[1] ?? '').toLowerCase() : '';
  const DATES: Array<[string, string]> = [[first + '’s birthday', 'Mar 14'], ['Your birthday', 'Oct 2'], ['Their anniversary', 'Jun 9']];
  const click = (fn: () => void) => (e: Ev) => { pre(e); fn(); };
  const steps = [
    {
      title: 'Choose how you hear about her', why: 'Daily updates come the way you pick. You can change it any time in Settings.', mark: d1 ? '✓' : '1', numStyle: num(d1, 0),
      hasChoices: true, choices: CH.map((c) => ({ label: c[1], style: chip(s.channel === c[0]), onClick: click(() => opts.act.channel(c[0])) })), hasLocked: true,
      hasAction: !!s.channel && !s.testSent, action: 'Send me a test', onAction: click(opts.act.test), actionStyle: btn(next === 0),
      evidence: !s.channel ? 'Pick one to continue' : !s.testSent ? '' : s.testAckSeconds != null ? 'Got it by ' + chName + ' in ' + s.testAckSeconds + ' seconds' : 'Sending… check your ' + (s.channel === 'email' ? 'inbox' : 'phone'),
      evidenceStyle: ev(d1),
    },
    {
      title: 'Say hello to ' + first, why: 'Atlas reads your message to her out loud, or shows her your photo. You’ll see when she got it.', mark: d2 ? '✓' : '2', numStyle: num(d2, 1),
      hasChoices: !s.hello, choices: ([['Thinking of you, Mom', 'msg'], ['Send a photo', 'photo']] as const).map((c) => ({ label: c[0], style: chip(false), onClick: click(() => opts.act.hello(c[1])) })), hasLocked: false,
      hasAction: false, action: '', onAction: null, actionStyle: '',
      evidence: s.readAt ? (s.hello === 'photo' ? 'Shown to her at ' : 'Read to her at ') + new Date(s.readAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : s.hello ? 'Sent. Atlas will share it with her shortly…' : 'Tap one to send it now',
      evidenceStyle: ev(d2),
    },
    {
      title: 'Add a date to remember', why: 'Atlas reminds her a few days before, so she can call you. We remind you about hers.', mark: d3 ? '✓' : '3', numStyle: num(d3, 2),
      hasChoices: !d3, choices: DATES.map((c) => ({ label: c[0] + ' · ' + c[1], style: chip(false), onClick: click(() => opts.act.date(c[0], c[1])) })), hasLocked: false,
      hasAction: false, action: '', onAction: null, actionStyle: '',
      evidence: d3 ? 'Saved: ' + s.date + '. Atlas will remind her.' : '', evidenceStyle: ev(d3),
    },
  ];
  return {
    fsuShow: !opts.hidden && !s.complete,
    // V9 wording (design fsuHead).
    fsuHead: complete ? 'You’re all set with ' + first : 'Finish setting up · ' + first,
    fsuProgress: complete ? '' : n + ' of 3 · about ' + Math.max(1, 3 - n) + ' min',
    fsuShared: complete ? 'Want a brother or sister to see this too? You can invite them from Settings.' : 'You’ll see her visits and her mood, and you are told when something significant happens to her. Her private chats with Atlas stay private.',
    fsuRules: click(opts.act.rules),
    fsuBar: 'height:4px;border-radius:2px;background:' + (complete ? '#1F6B52' : '#2F5C97') + ';width:' + Math.round((n / 3) * 100) + '%;transition:width .5s ease;',
    fsuOpen: opts.open && !complete,
    fsuToggle: complete ? click(opts.act.finish) : click(opts.act.toggle),
    fsuToggleLabel: complete ? 'Done' : opts.open ? 'Collapse' : n ? 'Continue' : 'Start',
    fsuToggleStyle: complete ? 'color:#fff;background:#1F6B52;' : opts.open ? 'color:#22272E;background:rgba(34,39,46,.09);' : 'color:#fff;background:#2F5C97;',
    fsuCanHide: !complete,
    fsuHide: click(opts.act.hide),
    fsuSteps: steps,
  };
}

const TODO_TAG: Record<string, string> = { fall: 'FALL', med_change: 'MEDICATION', infection: 'INFECTION', hospital_transfer: 'HOSPITAL', significant_change: 'CHANGE' };
const todoTagStyle = (kind: string) => {
  const c = kind === 'fall' || kind === 'hospital_transfer' ? ['#B3261E', 'rgba(179,38,30,.10)'] : kind === 'infection' || kind === 'significant_change' ? ['#8A5A00', 'rgba(184,134,11,.12)'] : ['#264C7E', 'rgba(47,92,151,.12)'];
  return 'font-size:14px;font-weight:800;letter-spacing:.08em;border-radius:4px;padding:2px 6px;color:' + c[0] + ';background:' + c[1] + ';';
};
/** Who started it, as the design words each kind ("Reported by K. Ruiz, RN · Called you 6:20pm"). */
const todoWho = (kind: string, by: string, told: string | null) =>
  kind === 'med_change' ? 'Changed by ' + by : kind === 'infection' ? 'Started by ' + by : 'Reported by ' + by + (told ? ' · ' + told : '');

/**
 * The family's To do (design fpTodoVals, L37298): every change-in-condition notice as a
 * short checklist written by the facility, the Staying close nudges and the shared list.
 * Each tap is a write (POST /family/todo/...); the page shows what the server holds.
 */
export function fpTodoVals(M: FpModel, ui: FpUi, act: FpActs, first: string, famOpen: number) {
  const T = M.todo;
  const go = (tab: FpUi['tab']) => act.setUi({ tab });
  const events = (T?.events ?? []).map((ev) => {
    const notice = M.notices.find((n) => n.id === ev.id) ?? null;
    const steps = ev.steps ?? [];
    const done = steps.filter((s) => s.done).length, all = steps.length;
    const allDone = all > 0 && done === all;
    const who = ev.redacted ? (T?.askName ? 'Ask ' + T.askName + ' what happened' : 'Ask the care team what happened') : todoWho(ev.kind, ev.by, notice?.told ?? null);
    return {
      key: ev.id, tag: TODO_TAG[ev.kind] ?? 'NOTICE', title: ev.title, when: ev.when, who, done: allDone,
      prog: ev.steps === null ? (ev.redacted ? 'Details go to the people ' + first + ' named' : 'Steps not written yet') : allDone ? 'All done' : done + ' of ' + all + ' done',
      progColor: allDone ? '#1F6B52' : ev.steps === null ? '#54606C' : '#22272E',
      tagStyle: todoTagStyle(ev.kind),
      openSteps: steps.filter((s) => !s.done).map((s) => ({
        t: s.t, d: s.d, act: s.act ?? '', canAct: !s.facility && !!s.act,
        onAct: (e: Ev) => {
          pre(e);
          // The step is ticked on the server; reading, asking and planning also open the section.
          if (s.kind === 'ack' && notice && !notice.mine) act.noticeSeen(notice);
          act.stepDone(s.id);
          if (s.kind === 'read') go('today');
          else if (s.kind === 'visit') go('visits');
          else if (s.kind === 'ask') go('messages');
        },
        mark: '', color: '#22272E',
        markStyle: 'flex:0 0 26px;width:26px;height:26px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;box-sizing:border-box;border:2px solid rgba(34,39,46,.3);',
      })),
    };
  }).sort((a, b) => (a.done ? 1 : 0) - (b.done ? 1 : 0));
  const openSteps = (T?.events ?? []).reduce((a, ev) => a + (ev.steps ?? []).filter((s) => !s.done).length, 0);
  const nudges = (T?.nudges ?? []).map((n) => {
    const line = n.kind === 'no_visit'
      ? { t: (n.who ?? 'Someone') + ' hasn’t seen her in ' + n.days + ' days', d: 'Suggest a visit or a call?', act: 'Ask ' + (n.who ?? 'them') }
      : n.kind === 'birthday'
        ? { t: (n.label ?? 'A family date') + ' is ' + (n.date ?? ''), d: first + 'Atlas will remind her to call. Want to set it up together?', act: 'Set it up' }
        : { t: 'Send her a photo this week', d: 'Photos show on her Atlas screen.', act: 'Send a photo' };
    return {
      ...line, open: !n.doneAt, isDone: !!n.doneAt, doneLine: 'Done ' + (n.doneAt ?? ''),
      onAct: (e: Ev) => { pre(e); if (n.kind === 'photo') go('moments'); else act.nudgeDone(n.key); },
    };
  });
  return {
    fpTasksOpen: String(openSteps + famOpen),
    fpTdLine: openSteps ? openSteps + ' steps after recent changes in her health' : 'You’re up to date on her health',
    fpTdEvents: events, fpTdNudges: nudges,
    fpTdNote: 'Health details show only to the people ' + first + ' named as her representatives. Others in the family see that something happened and who to ask. In an emergency the facility calls you; never wait for the app.',
    fpTdNew: ui.tdNew,
    fpTdNewChange: (e: { target: { value: string } }) => act.setUi({ tdNew: e.target.value }),
    fpTdAdd: (e: Ev) => { pre(e); const t = ui.tdNew.trim(); if (!t) return; act.taskAdd(t); },
  };
}

/**
 * Staff reading the Family Portal (design fpToldRows, L42209): whether each notice reached
 * the family. Seen is a family member's own receipt (family_notice_ack); "Call" logs the
 * call (POST /family/:residentId/notices/:id/called).
 */
export function fpToldVals(M: FpModel, ui: FpUi, act: FpActs) {
  const rows = M.notices.map((n) => {
    const seenBy = n.seenBy.find((x) => x.seen) ?? null;
    const called = ui.called[n.id] ?? null;
    const kind = n.kind ?? (n.med ? 'med_change' : n.sev === 'high' ? 'fall' : 'significant_change');
    return {
      seen: !!seenBy, tag: TODO_TAG[kind] ?? 'NOTICE', title: n.k, sub: n.when + ' · ' + n.by + ' · ' + n.told,
      tagStyle: 'flex:0 0 auto;' + todoTagStyle(kind),
      stat: seenBy ? (seenBy.short ?? seenBy.name) + ' confirmed ' + (seenBy.at ?? '') : called ? 'Called the family ' + called : 'Not confirmed by the family',
      statColor: seenBy || called ? '#1F6B52' : '#B3261E',
      needsCall: !seenBy && !called, callLabel: 'Call the family',
      onCall: (e: Ev) => { pre(e); act.noticeCalled(n); },
    };
  });
  const open = rows.filter((r) => r.needsCall).length;
  return { fpToldRows: rows, fpToldLine: open ? open + ' notice' + (open === 1 ? '' : 's') + ' the family hasn’t confirmed' : 'Every notice reached the family' };
}
